import { useEffect, useRef, useCallback, useState } from 'react';
import { type Message, type MessageType } from '@sheshdash/shared';

interface UseWebSocketOptions {
  onMessage: (message: Message) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  onBeforeConnect?: () => Promise<void>;
}

type WebSocketURLFactory = string | (() => string);
const wsDebug = import.meta.env.DEV;

export function useWebSocket(urlFactory: WebSocketURLFactory, options: UseWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const connectingRef = useRef(false);
  const optionsRef = useRef(options);
  const pendingMessagesRef = useRef<Message[]>([]);
  const socketSeqRef = useRef(0);

  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback(async () => {
    if (!shouldReconnectRef.current || connectingRef.current) {
      return;
    }
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    connectingRef.current = true;
    try {
      if (optionsRef.current.onBeforeConnect) {
        await optionsRef.current.onBeforeConnect();
      }
      if (!shouldReconnectRef.current) {
        connectingRef.current = false;
        return;
      }
      const url = typeof urlFactory === 'function' ? urlFactory() : urlFactory;
      const socketSeq = ++socketSeqRef.current;
      if (wsDebug) {
        console.log('[ws] creating socket', { socketSeq, url });
      }
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        if (wsDebug) {
          console.log('[ws] connected', { socketSeq, url });
        }
        setIsConnected(true);
        connectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        optionsRef.current.onOpen?.();

        // Flush any queued messages that were sent before the socket was ready.
        if (pendingMessagesRef.current.length > 0) {
          for (const message of pendingMessagesRef.current) {
            ws.send(JSON.stringify(message));
          }
          pendingMessagesRef.current = [];
        }
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        if (wsDebug) {
          console.log('[ws] disconnected', {
            socketSeq,
            url,
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            pendingMessages: pendingMessagesRef.current.length,
          });
        }
        setIsConnected(false);
        wsRef.current = null;
        connectingRef.current = false;
        optionsRef.current.onClose?.();

        // Auto-reconnect with exponential backoff
        if (!shouldReconnectRef.current) {
          return;
        }
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current++;

        reconnectTimeoutRef.current = setTimeout(() => {
          if (!shouldReconnectRef.current) {
            return;
          }
          if (wsDebug) {
            console.log('[ws] reconnect attempt', {
              nextAttempt: reconnectAttemptsRef.current,
              delayMs: delay,
            });
          }
          connect();
        }, delay);
      };

      ws.onerror = (error) => {
        if (wsRef.current !== ws) return;
        console.error('[ws] error', { socketSeq, url, error });
        optionsRef.current.onError?.(error);
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        try {
          const message: Message = JSON.parse(event.data);
          optionsRef.current.onMessage(message);
        } catch (error) {
          console.error('Failed to parse message:', error);
        }
      };
    } catch (error) {
      connectingRef.current = false;
      console.error('Failed to create WebSocket:', error);
    }
  }, [urlFactory]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      connectingRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.close();
      }
    };
  }, [connect]);

  // Send an explicit close-frame before the tab unloads / reloads.
  // Without this the browser tears the socket down via TCP teardown, which
  // leaves the server holding the player's slot for several seconds — long
  // enough that the *new* tab's auto-rejoin lands while the old slot still
  // looks active and gets rejected with "player name is already taken".
  useEffect(() => {
    const handleUnload = () => {
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(1000, 'page-unload'); } catch { /* ignore */ }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, []);

  const sendMessage = useCallback((type: MessageType, payload: Message['payload']) => {
    const message: Message = { type, payload };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      if (wsDebug && type === 'TOGGLE_PAUSE') {
        console.log('[ws] send TOGGLE_PAUSE', { payload });
      }
      wsRef.current.send(JSON.stringify(message));
      return;
    }

    // Queue messages while the socket is connecting/reconnecting.
    pendingMessagesRef.current.push(message);
    if (pendingMessagesRef.current.length > 50) {
      pendingMessagesRef.current.shift();
    }
    if (wsDebug) {
      console.warn('[ws] message queued', {
        type,
        payload,
        readyState: wsRef.current?.readyState ?? 'none',
        queued: pendingMessagesRef.current.length,
      });
    }
  }, []);

  return { sendMessage, isConnected };
}
